import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  Card,
  ErrorState,
  Field,
  Money,
  PageHeader,
  Select,
  TextField,
} from "../components/ui";
import { dollarsToCents, today } from "../lib/format";
import type { Account, Vendor } from "../lib/models";
import { useInvoicePilot } from "./InvoicePilotLink";

interface LineDraft {
  description: string;
  qty: string;
  unitCost: string;
  expenseAccountId: string;
}

const blankLine: LineDraft = {
  description: "",
  qty: "1",
  unitCost: "0.00",
  expenseAccountId: "",
};

export default function BillForm() {
  const { currentOrg } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const pilot = useInvoicePilot();

  const { data: vendors } = useQuery({
    queryKey: ["vendors", currentOrg?.id],
    queryFn: () => api.get<Vendor[]>("/vendors"),
    enabled: !!currentOrg,
  });
  const { data: accounts } = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
    enabled: !!currentOrg,
  });
  const expenseAccounts = (accounts ?? []).filter(
    (a) => a.type === "expense" || a.type === "asset",
  );

  const [header, setHeader] = useState({
    vendorId: "",
    docNumber: "",
    txnDate: today(),
    dueDate: "",
    memo: "",
  });
  const [lines, setLines] = useState<LineDraft[]>([{ ...blankLine }]);

  const total = lines.reduce(
    (s, l) => s + Math.round(Number(l.qty || 0) * dollarsToCents(l.unitCost)),
    0,
  );

  function updateLine(i: number, patch: Partial<LineDraft>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/bills", {
        vendorId: header.vendorId,
        docNumber: header.docNumber || undefined,
        txnDate: header.txnDate,
        dueDate: header.dueDate || undefined,
        memo: header.memo || undefined,
        lines: lines.map((l) => ({
          description: l.description || undefined,
          qty: Number(l.qty || 0),
          unitCost: dollarsToCents(l.unitCost),
          expenseAccountId: l.expenseAccountId,
        })),
      }),
    onSuccess: (bill) => {
      qc.invalidateQueries({ queryKey: ["bills"] });
      navigate(pilot.data?.enabled ? `/bills?bill=${encodeURIComponent(bill.id)}` : "/bills");
    },
  });

  const valid =
    header.vendorId && lines.every((l) => l.expenseAccountId) && total >= 0;

  return (
    <div>
      <PageHeader title="Enter bill" subtitle="Record an amount you owe a vendor" />
      <Card className="p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Field required label="Vendor">
            <Select
              value={header.vendorId}
              onChange={(e) => setHeader({ ...header, vendorId: e.target.value })}
            >
              <option value="">— select —</option>
              {(vendors ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.displayName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Vendor bill number (optional)" hint="Enter the invoice or bill number shown on the vendor document. Leave blank to assign a number automatically.">
            <TextField
              placeholder="Number on vendor document"
              value={header.docNumber}
              onChange={(e) => setHeader({ ...header, docNumber: e.target.value })}
            />
          </Field>
          <Field required label="Date">
            <TextField
              type="date"
              value={header.txnDate}
              onChange={(e) => setHeader({ ...header, txnDate: e.target.value })}
            />
          </Field>
          <Field label="Due date">
            <TextField
              type="date"
              value={header.dueDate}
              onChange={(e) => setHeader({ ...header, dueDate: e.target.value })}
            />
          </Field>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="th">Category / account (required)</th>
                <th className="th">Description</th>
                <th className="th w-20 text-right">Qty</th>
                <th className="th w-32 text-right">Cost</th>
                <th className="th w-28 text-right">Amount</th>
                <th className="th w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const amount = Math.round(
                  Number(l.qty || 0) * dollarsToCents(l.unitCost),
                );
                return (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 pr-2">
                      <Select
                        aria-label={`Category / account, line ${i + 1}`} aria-required="true" value={l.expenseAccountId}
                        onChange={(e) =>
                          updateLine(i, { expenseAccountId: e.target.value })
                        }
                      >
                        <option value="">— select —</option>
                        {expenseAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="py-2 pr-2">
                      <TextField
                        value={l.description}
                        onChange={(e) =>
                          updateLine(i, { description: e.target.value })
                        }
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <TextField
                        type="number"
                        className="text-right"
                        value={l.qty}
                        onChange={(e) => updateLine(i, { qty: e.target.value })}
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <TextField
                        type="number"
                        step="0.01"
                        className="text-right"
                        value={l.unitCost}
                        onChange={(e) =>
                          updateLine(i, { unitCost: e.target.value })
                        }
                      />
                    </td>
                    <td className="py-2 pr-2 text-right">
                      <Money cents={amount} currency={currency} />
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-gray-300 hover:text-red-500"
                        onClick={() =>
                          setLines((ls) => ls.filter((_, idx) => idx !== i))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setLines((ls) => [...ls, { ...blankLine }])}
          >
            + Add line
          </Button>
          <div className="text-right">
            <span className="mr-3 text-sm text-gray-500">Total</span>
            <span className="text-xl font-semibold">
              <Money cents={total} currency={currency} />
            </span>
          </div>
        </div>

        {create.error && (
          <div className="mt-4">
            <ErrorState error={create.error} />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate("/bills")}>
            Cancel
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Saving…" : "Save bill"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
